import type { Conditions } from '@accra/shared';
import type { Group, WebGLRenderer } from 'three';
import type { DoorRig } from '../entities/vehicles/doorRig';
import type { Paint } from '@accra/shared';
import { PLAYER_SPECS, playerTrotro, type PlayerModel } from '../entities/vehicles/catalog';
import { ChaseCamera } from '../render/camera/chaseCamera';
import { Atmosphere } from '../render/atmosphere/atmosphere';
import { FuelStationView } from '../world/road/fuelStationView';
import { CheckpointView } from './checkpointView';
import { createScene, followSun, setShadowQuality, type SceneRig } from '../render/scene';
import { Hills } from '../world/backdrop/hills';
import { Skyline } from '../world/backdrop/skyline';
import { reliefAt } from '../world/terrain';
import { createStreetProps, propMeshes } from '../world/props/streetProps';
import { Crowd } from '../world/road/crowd';
import { RoadTiles, TILE_COUNT } from '../world/road/roadTiles';
import { StopMarkers } from '../world/road/stopMarkers';
import { Walkers, type DoorInfo } from '../world/road/walkers';
import { ZebraCrossers } from '../world/road/zebraCrossers';
import { BonusPickupsView } from '../world/props/bonusPickupsView';
import { PickupsView } from '../world/props/pickupsView';
import { AdBoards } from '../world/ads/adBoards';
import { AdTracker } from './adTracker';
import type { AdImpression, AdPlacement } from '@accra/shared';
import type { RunState } from './RunState';
import { TrafficView } from './trafficView';
import { CrossTrafficView } from './crossTrafficView';
import { OncomingView } from './oncomingView';

/** Everything 3D for a run. Reads RunState; never changes it. */
export class RunView {
  readonly chase = new ChaseCamera();
  private readonly rig: SceneRig;
  private readonly props = createStreetProps(TILE_COUNT);
  private readonly road = new RoadTiles(this.props);
  private readonly skyline = new Skyline();
  private readonly hills = new Hills();
  private readonly stops = new StopMarkers();
  private readonly crowd = new Crowd();
  private readonly walkers = new Walkers();
  private readonly zebra = new ZebraCrossers();
  private readonly pickups = new PickupsView();
  private readonly bonuses = new BonusPickupsView();
  private readonly ads = new AdBoards();
  private readonly atmosphere: Atmosphere;
  private readonly stations = new FuelStationView();
  private readonly checkpointView = new CheckpointView();
  private readonly adTracker = new AdTracker();
  /** Only count ad impressions while actually driving. */
  trackAds = false;
  private door: DoorInfo = { offsetZ: 0, halfWidth: 1 };
  private readonly traffic: TrafficView;
  private readonly crossTraffic: CrossTrafficView;
  private readonly oncoming: OncomingView;
  /** Completed U-turns seen so far: when it changes the camera jumps to the new lane. */
  private swaps = 0;
  private bus: Group | null = null;
  private doorRig: DoorRig | null = null;
  private busModel: PlayerModel | null = null;
  private busLook = '';
  private lean = 0;

  constructor(private readonly renderer: WebGLRenderer) {
    this.rig = createScene(renderer);
    this.traffic = new TrafficView(this.rig.scene);
    this.crossTraffic = new CrossTrafficView(this.rig.scene);
    this.oncoming = new OncomingView(this.rig.scene);
    this.atmosphere = new Atmosphere(this.rig, renderer, this.road);
    this.rig.scene.add(this.road.group, this.stops.group, this.crowd.group, this.walkers.group, this.zebra.group, this.pickups.group, this.bonuses.group, this.stations.group, this.checkpointView.group, this.ads.group, this.skyline.mesh, this.hills.group, ...propMeshes(this.props));
  }

  /** A picture of the road right now (the shift summary's background for the score card). Null if the browser refuses. */
  snapshot(): HTMLCanvasElement | null {
    // Drawing straight after rendering, in the same moment, is what keeps the WebGL image from being cleared.
    this.renderer.render(this.rig.scene, this.chase.camera);
    const source = this.renderer.domElement;
    const out = document.createElement('canvas');
    out.width = source.width;
    out.height = source.height;
    out.getContext('2d')?.drawImage(source, 0, 0);
    return out;
  }

  setPlayer(model: PlayerModel, sticker: string, paint: Paint): void {
    const look = `${model}|${sticker}|${paint.id}`;
    if (this.busLook === look && this.bus) return;
    this.busLook = look;
    if (this.bus) this.rig.scene.remove(this.bus);
    const player = playerTrotro(model, sticker, paint);
    this.bus = player.body;
    this.doorRig = player.door;
    this.busModel = model;
    const spec = PLAYER_SPECS[model];
    const doorU = spec.door ? (spec.door.u0 + spec.door.u1) / 2 : spec.length / 2;
    this.door = { offsetZ: spec.length / 2 - doorU, halfWidth: spec.width / 2 };
    this.rig.scene.add(this.bus);
    this.atmosphere.attachHeadlights(this.bus, spec.length, spec.width);
  }

  /** Weather and time of day for the shift (clear daylight for the menu). */
  setConditions(conditions: Conditions): void {
    this.atmosphere.apply(conditions);
  }

  setAds(placements: readonly AdPlacement[]): void {
    this.ads.setPlacements(placements);
  }

  startAdTracking(): void {
    this.adTracker.reset();
    this.trackAds = true;
  }

  stopAdTracking(): AdImpression[] {
    this.trackAds = false;
    return this.adTracker.impressions();
  }

  setShadowQuality(mapSize: number): void {
    setShadowQuality(this.rig, mapSize);
  }

  reset(): void {
    this.road.invalidate();
    this.stops.invalidate();
    this.crowd.invalidate();
    this.traffic.clear();
    this.crossTraffic.clear();
    this.oncoming.clear();
    this.swaps = 0;
  }

  render(state: RunState, dt: number): void {
    const { bus } = state;
    const z = -bus.s;
    this.road.update(bus.s, state.stats.seconds);
    const relief = reliefAt(bus.s);
    this.skyline.update(z, 1 - relief);
    this.hills.update(z, relief);
    this.stops.update(state.stop.seq);
    this.crowd.update(state.stop.seq, state.stop.waiting, dt);
    this.walkers.update(state, this.door);
    this.traffic.sync(state.traffic);
    this.crossTraffic.sync(state.crossTraffic);
    this.oncoming.sync(state.oncoming);
    this.zebra.update(state.zebra, dt);
    this.pickups.sync(state.pickups, dt);
    this.bonuses.sync(state.pickups, dt);
    this.stations.update(bus.s);
    this.checkpointView.update(state.checkpoint.current, bus, dt);

    if (this.bus) {
      // Body roll leans away from the turn; the nose points where the trotro is going.
      const roll = Math.max(-0.07, Math.min(0.07, bus.lateralAccel * 0.012));
      this.lean += (roll - this.lean) * Math.min(1, dt * 6);
      if (!Number.isFinite(this.lean)) this.lean = 0;
      this.bus.position.set(bus.x, bus.y, z);
      this.bus.rotation.z = this.lean;
      this.bus.rotation.y = -bus.yaw;
      this.bus.rotation.x = bus.y > 0 ? -0.05 : 0;
    }
    this.doorRig?.update(state.stop.stopped && bus.speed < 1, dt);

    followSun(this.rig, z);
    this.atmosphere.update(dt, state, this.chase.camera);
    if (state.turn.swaps !== this.swaps) {
      this.swaps = state.turn.swaps;
      this.chase.snap(bus.x);
    }
    this.chase.follow(bus.x, bus.y, z, bus.speed, dt, state.turn.heading);
    const boards = this.ads.update(bus.s, dt);
    if (this.trackAds) this.adTracker.update(boards, this.chase.camera, dt);
    this.renderer.render(this.rig.scene, this.chase.camera);
  }
}
